import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { motion, AnimatePresence } from 'framer-motion';

const CHAT_API = import.meta.env.VITE_CHAT_API_URL || '/api/chat';

const Chat: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    messages,
    sendMessage,
    status,
    stop,
    error,
    regenerate,
    clearError,
  } = useChat({
    transport: new DefaultChatTransport({ api: CHAT_API }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || status !== 'ready') return;
    sendMessage({ text });
    setInput('');
  };

  const isLoading = status === 'submitted' || status === 'streaming';

  // Auto-scroll to bottom when messages change or while streaming; focus input when done
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    if (open && !isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages, isLoading, open]);

  return (
    <>
      {/* Floating button */}
      <motion.button
        type="button"
        className="chat-fab"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? (
          <i className="fas fa-times" />
        ) : (
          <i className="fas fa-comments" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="chat-header">
              <span className="chat-title">
                <i className="fas fa-robot" style={{ marginRight: 8 }} />
                AI Assistant
              </span>
              <button
                type="button"
                className="chat-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div ref={messagesContainerRef} className="chat-messages">
              {messages.length === 0 && (
                <div className="chat-welcome">
                  <p>Ask me anything about this portfolio, Web3, or development.</p>
                </div>
              )}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`chat-message chat-message--${message.role}`}
                >
                  <span className="chat-message-role">
                    {message.role === 'user' ? 'You' : 'AI'}
                  </span>
                  <div className="chat-message-content">
                    {message.parts.map((part, idx) =>
                      part.type === 'text' ? (
                        <span key={idx}>{part.text}</span>
                      ) : null
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chat-message chat-message--assistant">
                  <span className="chat-message-role">AI</span>
                  <div className="chat-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="chat-error">
                <span>Something went wrong.</span>
                <button type="button" onClick={() => regenerate()}>
                  Retry
                </button>
                <button type="button" onClick={clearError}>
                  Dismiss
                </button>
              </div>
            )}

            <form className="chat-form" onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                disabled={status !== 'ready'}
                className="chat-input"
              />
              <div className="chat-actions">
                {isLoading ? (
                  <button
                    type="button"
                    className="chat-btn chat-btn--stop"
                    onClick={stop}
                  >
                    Stop
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="chat-btn chat-btn--send"
                    disabled={!input.trim() || status !== 'ready'}
                  >
                    <i className="fas fa-paper-plane" />
                  </button>
                )}
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chat;
